import {
    ChangeDetectionStrategy,
    Component,
    computed,
    forwardRef,
    input,
    signal,
} from '@angular/core'
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'

function startOfToday(): Date {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
}

function normalizeDate(value: unknown): Date | null {
    if (value == null || value === '') {
        return null
    }
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value
    }
    const d = new Date(value as string)
    return Number.isNaN(d.getTime()) ? null : d
}

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'date-set-today-toggle',
    templateUrl: './date-set-today-toggle.component.html',
    styleUrls: ['./date-set-today-toggle.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DateSetTodayToggleComponent),
            multi: true,
        },
    ],
    standalone: true,
    imports: [FormsModule, WjInputModule],
})
export class DateSetTodayToggleComponent implements ControlValueAccessor {
    /** Forwarded to `wj-input-date` for label association (`for="..."`). */
    readonly inputId = input('')
    /** Optional; defaults to `inputId` when empty. */
    readonly inputName = input('')
    readonly isRequired = input(false)
    readonly checkboxAriaLabel = input('Set date')

    protected readonly dateValue = signal<Date | null>(null)
    protected readonly controlDisabled = signal(false)

    readonly isSet = computed(() => this.dateValue() != null)
    readonly toggleId = computed(() => {
        const id = this.inputId()
        return id ? `${id}-toggle` : ''
    })
    readonly wjName = computed(() => this.inputName() || this.inputId() || 'dateSetToday')

    private onChange: (value: Date | null) => void = () => undefined
    private onTouchedFn: () => void = () => undefined

    writeValue(value: unknown): void {
        this.dateValue.set(normalizeDate(value))
    }
    registerOnChange(fn: (value: Date | null) => void): void {
        this.onChange = fn
    }
    registerOnTouched(fn: () => void): void {
        this.onTouchedFn = fn
    }
    setDisabledState(isDisabled: boolean): void {
        this.controlDisabled.set(isDisabled)
    }

    protected onCheckboxChange(checked: boolean): void {
        if (this.controlDisabled()) {
            return
        }
        if (checked) {
            if (this.dateValue() == null) {
                this.dateValue.set(startOfToday())
                this.emit()
            }
        } else {
            this.dateValue.set(null)
            this.emit()
        }
        this.onTouchedFn()
    }

    protected onPickerChange(value: Date | null | undefined): void {
        if (this.controlDisabled()) {
            return
        }
        if (value == null || (value instanceof Date && Number.isNaN(value.getTime()))) {
            this.dateValue.set(null)
        } else {
            this.dateValue.set(value)
        }
        this.emit()
        this.onTouchedFn()
    }

    private emit(): void {
        this.onChange(this.dateValue())
    }
}
